import { useState, useCallback } from 'react';
import { logger } from '@core/utils';
import { useGetFilesQuery, useLazyGetDownloadUrlQuery, useDeleteFileMutation } from '../api/filesApi';

/**
 * Custom hook for file operations (list, download, delete)
 * Encapsulates all business logic for file management
 *
 * @param {Object} params
 * @param {Function} params.t - Translation function from react-i18next
 * @param {string} [params.userId] - Filter files by user ID
 * @param {string} [params.segmentedKnowledgeId] - Filter files by segmented knowledge ID
 * @param {string} [params.folderId] - Filter files by folder ID (null for root, undefined for all)
 *
 * @returns {Object} File operations and state
 *
 * @example
 * const { files, isLoading, downloadFile, deleteFile, downloadingFile, error, successMessage } = useFileOperations({ t, userId });
 */
export function useFileOperations({ t, userId = null, segmentedKnowledgeId = null, folderId = undefined }) {
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // RTK Query hooks - pass filter parameters
  const { data: files = [], isLoading, refetch } = useGetFilesQuery({ userId, segmentedKnowledgeId, folderId });
  const [getDownloadUrl] = useLazyGetDownloadUrlQuery();
  const [deleteFileMutation] = useDeleteFileMutation();

  /**
   * Download a file
   * @param {Object} file - File object to download
   */
  const downloadFile = useCallback(async (file) => {
    try {
      setDownloadingFile(file.id);

      // Get presigned download URL
      const result = await getDownloadUrl(file.id).unwrap();
      const downloadUrl = result.download_url;

      if (!downloadUrl) {
        throw new Error('No download URL provided');
      }

      // Create temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.original_filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      logger.error('Download error:', err);
      setError(t('file.downloadError', 'Failed to download file'));
    } finally {
      setDownloadingFile(null);
    }
  }, [getDownloadUrl, t]);

  /**
   * Delete a file
   * @param {string} fileId - ID of file to delete
   */
  const deleteFile = useCallback(async (fileId) => {
    try {
      await deleteFileMutation(fileId).unwrap();
      setSuccessMessage(t('file.deleteSuccess', 'File deleted successfully'));
      // Files list will automatically refresh via RTK Query cache invalidation
    } catch (err) {
      logger.error('Delete error:', err);
      setError(t('file.deleteError', 'Failed to delete file'));
    }
  }, [deleteFileMutation, t]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clear success message
   */
  const clearSuccess = useCallback(() => {
    setSuccessMessage(null);
  }, []);

  return {
    files,
    isLoading,
    downloadFile,
    deleteFile,
    downloadingFile,
    error,
    successMessage,
    clearError,
    clearSuccess,
    refetch,
  };
}
